import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '@chainlit/react-client';
import { useProfileManagement } from '@chainlit/react-client/src/api/hooks/auth/profileManagement';

import { useTranslation } from '@/components/i18n/Translator';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ProfileEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ProfileEditDialog = ({ open, onOpenChange }: ProfileEditDialogProps) => {
  const { user } = useAuth();
  const { updateProfile, updatePassword } = useProfileManagement();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    display_name: user?.display_name || user?.identifier || ''
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Error states
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Success states
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setProfileError('');
    setProfileSuccess(false);

    const result = await updateProfile({
      display_name: profileForm.display_name
    });

    if (result.success) {
      setProfileSuccess(true);
      setTimeout(() => {
        setProfileSuccess(false);
        onOpenChange(false);
      }, 2000);
    } else {
      setProfileError(result.error || 'Failed to update profile');
    }

    setLoading(false);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setPasswordError('');
    setPasswordSuccess(false);

    // Validate passwords match
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (passwordForm.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    const result = await updatePassword({
      current_password: passwordForm.current_password,
      new_password: passwordForm.new_password
    });

    if (result.success) {
      setPasswordSuccess(true);
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setTimeout(() => {
        setPasswordSuccess(false);
        onOpenChange(false);
      }, 2000);
    } else {
      setPasswordError(result.error || 'Failed to update password');
    }

    setLoading(false);
  };

  const resetForm = () => {
    setProfileForm({
      display_name: user?.display_name || user?.identifier || ''
    });
    setPasswordForm({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setProfileError('');
    setPasswordError('');
    setProfileSuccess(false);
    setPasswordSuccess(false);
    setActiveTab('profile');
  };

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      resetForm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('profile.editDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('profile.editDialog.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Tab Navigation */}
        <div className="flex space-x-1 border-b">
          <Button
            variant={activeTab === 'profile' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('profile')}
            className="flex-1"
          >
            {t('profile.editDialog.tabs.profile')}
          </Button>
          <Button
            variant={activeTab === 'password' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('password')}
            className="flex-1"
          >
            {t('profile.editDialog.tabs.password')}
          </Button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="display_name">
                {t('profile.editDialog.fields.displayName')}
              </Label>
              <Input
                id="display_name"
                value={profileForm.display_name}
                onChange={(e) =>
                  setProfileForm({
                    ...profileForm,
                    display_name: e.target.value
                  })
                }
                placeholder={t('profile.editDialog.placeholders.displayName')}
                required
              />
            </div>

            {profileError && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {profileError}
              </div>
            )}

            {profileSuccess && (
              <div className="text-sm text-green-600 bg-green-50 p-2 rounded">
                {t('profile.editDialog.messages.profileUpdated')}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t('common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? t('common.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">
                {t('profile.editDialog.fields.currentPassword')}
              </Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      current_password: e.target.value
                    })
                  }
                  placeholder={t(
                    'profile.editDialog.placeholders.currentPassword'
                  )}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_password">
                {t('profile.editDialog.fields.newPassword')}
              </Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      new_password: e.target.value
                    })
                  }
                  placeholder={t('profile.editDialog.placeholders.newPassword')}
                  required
                  minLength={8}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">
                {t('profile.editDialog.fields.confirmPassword')}
              </Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirm_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      confirm_password: e.target.value
                    })
                  }
                  placeholder={t(
                    'profile.editDialog.placeholders.confirmPassword'
                  )}
                  required
                  minLength={8}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {passwordError && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="text-sm text-green-600 bg-green-50 p-2 rounded">
                {t('profile.editDialog.messages.passwordUpdated')}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t('common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? t('common.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileEditDialog;
